import { useCallback } from 'react';
import { api } from '../api/client';
import { useToast } from '../components/Toast';

/**
 * Aviso de los formularios (2026-11-02): después de confirmar una OV, registrar un movimiento de stock o confirmar un remito,
 * si el cambio no se va a reflejar en alguna publicación de Mercado Libre (p. ej. un combo que no sincroniza), un aviso que no
 * bloquea. Lo decide GET ml/sync-sku/impacto (no lee Mercado Libre). Si la consulta falla (sin permiso, servidor caído), no
 * se avisa nada: la operación ya se hizo.
 */

/** Tope de SKU por consulta (MAX_SKUS_IMPACTO en el backend). */
export const MAX_SKUS_IMPACTO = 100;
/** El aviso es largo: queda más que los 5 s de un aviso común. */
const DURACION_AVISO_MS = 20_000;
/** Publicaciones que se nombran en el aviso; del resto, cuántas más. */
const NOMBRADAS = 3;

interface ImpactoMl {
  noSeActualizan: Array<{ mlaId: string; motivo: string; resolver: string | null }>;
}

/** "Ojo: esto no se va a reflejar en Mercado Libre en …" o null si todas se actualizan. */
export function mensajeImpactoMl(r: ImpactoMl | null | undefined): string | null {
  const lista = r?.noSeActualizan ?? [];
  if (lista.length === 0) return null;
  const nombradas = lista.slice(0, NOMBRADAS).map((p) => `${p.mlaId} (${p.motivo})`).join(', ');
  const mas = lista.length > NOMBRADAS ? ` y ${lista.length - NOMBRADAS} más` : '';
  const donde = [...new Set(lista.map((p) => p.resolver).filter((d): d is string => !!d))];
  const cuantas = lista.length === 1 ? '1 publicación' : `${lista.length} publicaciones`;
  return `Ojo: esto no se va a reflejar en Mercado Libre en ${cuantas}: ${nombradas}${mas}.`
    + (donde.length > 0 ? ` Para resolverlo: ${donde.join(' o ')}.` : '');
}

/** Devuelve `avisar(skus)`: consulta el impacto en segundo plano y, sólo si algo no se actualiza, muestra el aviso. */
export function useAvisoImpactoMl() {
  const { addToast } = useToast();
  return useCallback((skus: Array<string | null | undefined>) => {
    const unicos = [...new Set(skus.map((s) => (s ?? '').trim()).filter((s) => s !== ''))].slice(0, MAX_SKUS_IMPACTO);
    if (unicos.length === 0) return;
    api.get<ImpactoMl>(`/api/v3/ml/sync-sku/impacto?${new URLSearchParams({ skus: unicos.join(',') })}`)
      .then((r) => {
        const mensaje = mensajeImpactoMl(r);
        if (mensaje) addToast(mensaje, 'info', DURACION_AVISO_MS);
      })
      .catch(() => { /* el aviso es un extra: si no se puede consultar, no se avisa */ });
  }, [addToast]);
}
